'use client';

import { useCallback, useId, useRef, useState } from 'react';

import {
  PRIZES,
  Prize,
  PrizeId,
  SEGMENT_ANGLE,
  drawPrize,
  spinRotation,
} from '@/lib/prize-wheel/prizes';

const C = 200;
const DESKTOP_SHRINK = 1 / 1.3109;

const PETAL_D =
  'M8.39775 0C2.69693 0 -1.34476 5.56279 0.416884 10.9846L50.8588 166.228C52.3123 170.701 57.0078 172.946 61.0775 171.332C94.1973 158.194 122.997 135.896 144.036 106.938C165.075 77.9806 177.381 43.7003 179.642 8.14178C179.92 3.77227 176.334 0 171.63 0H8.39775Z';
const APEX = { x: -8.404, y: -3.815 };
const BISECTOR = 36;

const REST_OFFSET = -60;
const HUB_R = 43.5;
const HUB_STROKE = 6;

const HUB = { x: 199.46, y: 220.5 };
const from = (x: number, y: number) => ({ x: x - HUB.x, y: y - HUB.y });

const POINTER = { x: C - 0.03 - 15.011 - 9.902, y: C - 196.5 - 6.398, w: 49.82, h: 55.3, tipY: C - 160 };

const TALL = { dy1: 6.5, dy2: 25.8 };
const SHORT = { dy1: -1, dy2: 17.3 };

interface SegmentArt {
  label: { x: number; y: number; rotate: number; dy1: number; dy2: number };
  statue: { x: number; y: number; w: number; h: number; rotate: number; flipY: boolean };
}

const DIM = 0.4;
const EMPHASIS_TRANSITION = 'opacity 350ms ease';

const ART: Record<PrizeId, SegmentArt> = {
  '50k-challenge': {
    label: { ...from(137.58, 34.53 + 79.635 / 2), rotate: -26.63, ...TALL },
    statue: { ...from(60.48 + 266.604 / 2, 72.74 + 255.121 / 2), w: 211.138, h: 190.638, rotate: 158.33, flipY: true },
  },
  '10-voucher': {
    label: { ...from(313.36, 93.31 + 65.298 / 2), rotate: 49.67, ...SHORT },
    statue: { ...from(43.1 + 351.137 / 2, 39.87 + 347.474 / 2), w: 283.005, h: 211.31, rotate: -137.07, flipY: true },
  },
  '100k-challenge': {
    label: { ...from(338.27, 266.81 + 82.342 / 2), rotate: 122.63, ...TALL },
    statue: { ...from(115.74 + 241.457 / 2, 118.8 + 238.079 / 2), w: 151.606, h: 188.201, rotate: -48.74, flipY: true },
  },
  '50k-funded': {
    label: { ...from(169.43, 343.38 + 66.908 / 2), rotate: -168.29, ...TALL },
    statue: { ...from(35.94 + 307.479 / 2, 117.26 + 253.163 / 2), w: 272.943, h: 203.798, rotate: 11.26, flipY: true },
  },
  '100k-funded': {
    label: { ...from(47.1, 211.75 + 53.791 / 2), rotate: -95.88, ...TALL },
    statue: { ...from(65.33 + 167.902 / 2, 131.68 + 216.855 / 2), w: 207.2, h: 154.71, rotate: -93.74, flipY: false },
  },
};

const petalTransform = (i: number) =>
  `translate(${C} ${C}) rotate(${REST_OFFSET - 90 + i * SEGMENT_ANGLE}) translate(${-APEX.x} ${-APEX.y})`;

const SPIN_EASE = 'cubic-bezier(0.16, 0.84, 0.16, 1)';
const SPIN_MS = 4200;
const SPIN_TURNS = 5;

export interface PrizeWheelProps {
  onResult?: (prize: Prize) => void;
  spinLabel?: string;
  className?: string;
}

export default function PrizeWheel({
  onResult,
  spinLabel = 'SPIN',
  className,
}: PrizeWheelProps) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [landed, setLanded] = useState<Prize | null>(null);
  const [highlight, setHighlight] = useState<PrizeId | 'all' | null>('all');
  const pending = useRef<Prize | null>(null);

  const settle = useCallback(() => {
    const prize = pending.current;
    pending.current = null;
    setSpinning(false);
    if (prize) {
      setLanded(prize);
      setHighlight(prize.id);
      onResult?.(prize);
    }
  }, [onResult]);

  const spin = useCallback(() => {
    if (spinning) return;

    const prize = drawPrize();
    pending.current = prize;
    setLanded(null);

    const jitter = Math.random() * 2 - 1;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    setRotation((current) =>
      spinRotation(current, prize, reduced ? 0 : SPIN_TURNS, jitter, REST_OFFSET),
    );

    if (reduced) {
      setSpinning(false);
      pending.current = null;
      setLanded(prize);
      setHighlight(prize.id);
      onResult?.(prize);
      return;
    }

    setHighlight(null);
    setSpinning(true);
  }, [onResult, spinning]);

  return (
    <div
      className={`relative isolate aspect-square w-full font-geist ${className ?? ''}`}
      style={{ containerType: 'inline-size' }}
    >
      <svg
        viewBox={`0 0 ${C * 2} ${C * 2}`}
        className="h-full w-full overflow-visible"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          {PRIZES.map((prize, i) => (
            <clipPath id={`${uid}-clip-${i}`} key={prize.id}>
              <path d={PETAL_D} transform={petalTransform(i)} />
            </clipPath>
          ))}
        </defs>

        <g
          style={{
            transform: `rotate(${rotation}deg)`,
            transformOrigin: `${C}px ${C}px`,
            transition: spinning ? `transform ${SPIN_MS}ms ${SPIN_EASE}` : undefined,
          }}
          onTransitionEnd={(e) => {
            if (e.propertyName === 'transform') settle();
          }}
        >
          {PRIZES.map((prize, i) => {
            const s = ART[prize.id].statue;
            return (
              <g
                key={prize.id}
                clipPath={`url(#${uid}-clip-${i})`}
                style={{ opacity: highlight === 'all' || highlight === prize.id ? 1 : DIM, transition: EMPHASIS_TRANSITION }}
              >
                <path d={PETAL_D} transform={petalTransform(i)} fill={prize.segmentFill} />
                <image
                  href={prize.statue}
                  x={-s.w / 2}
                  y={-s.h / 2}
                  width={s.w}
                  height={s.h}
                  preserveAspectRatio="xMidYMid slice"
                  transform={`translate(${C + s.x} ${C + s.y}) rotate(${s.rotate})${s.flipY ? ' scale(1 -1)' : ''}`}
                  style={{ mixBlendMode: 'luminosity' }}
                />
              </g>
            );
          })}

          {PRIZES.map((prize) => {
            const l = ART[prize.id].label;
            return (
              <g
                key={`label-${prize.id}`}
                transform={`translate(${C + l.x} ${C + l.y}) rotate(${l.rotate})`}
                fill={prize.segmentInk === 'dark' ? '#000000' : '#FFFFFF'}
                style={{ opacity: highlight === 'all' || highlight === prize.id ? 1 : DIM, transition: EMPHASIS_TRANSITION }}
              >
                <text
                  textAnchor="middle"
                  y={l.dy1}
                  fontWeight={500}
                  className="max-lg:[font-size:20.1px] max-lg:[letter-spacing:-1.2px] lg:[font-size:15.33px] lg:[letter-spacing:-0.92px]"
                >
                  {prize.segmentTop}
                </text>
                <text
                  textAnchor="middle"
                  y={l.dy2}
                  fontWeight={500}
                  className="max-lg:[font-size:13.2px] max-lg:[letter-spacing:-0.79px] lg:[font-size:10.07px] lg:[letter-spacing:-0.6px]"
                >
                  {prize.segmentBottom}
                </text>
              </g>
            );
          })}
        </g>

        <g className="lg:[transform:scale(0.763)]" style={{ transformOrigin: `${C}px ${C}px` }}>
          <circle cx={C} cy={C} r={HUB_R} fill="#000000" stroke="#FFFFFF" strokeWidth={HUB_STROKE} />
        </g>
        {/* Desktop also lifts it 9 units: the comp's tip sits ~24px inside the rim there, not 35. */}
        <g className="lg:[transform:translateY(-9px)_scale(0.763)]" style={{ transformOrigin: `${C}px ${POINTER.tipY}px` }}>
          <image href="/prize-wheel/pointer.svg" x={POINTER.x} y={POINTER.y} width={POINTER.w} height={POINTER.h} />
        </g>
      </svg>

      <button
        type="button"
        onClick={spin}
        disabled={spinning}
        aria-busy={spinning}
        className="absolute left-1/2 top-1/2 flex aspect-square -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black font-geist font-bold uppercase leading-none tracking-[-0.06em] text-white transition-transform duration-200 hover:scale-[1.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black disabled:cursor-default disabled:hover:scale-100 max-lg:w-[20.25%] max-lg:[font-size:4.75cqw] lg:w-[15.45%] lg:[font-size:3.62cqw]"
      >
        {spinLabel}
      </button>

      <p aria-live="polite" className="sr-only">
        {spinning
          ? 'Spinning the wheel.'
          : landed
            ? `You won ${landed.modalAmount} ${landed.modalQualifier}.`
            : ''}
      </p>
    </div>
  );
}
